import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { User } from '@common/models/user';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { ListItemModel } from '@progress/kendo-angular-buttons';
import { distinctUntilChanged, firstValueFrom } from 'rxjs';
import { QueryService } from './query.service';
import { RememberStateService } from './remember-state.service';
import { ToastrNotificationService } from './toastr-notification.service';
import { UserService } from './user.service';

export interface Language extends ListItemModel {
    languageId: string;
}

export enum LanguageIds {
    en = 'en',
    sl = 'sl'
}

@Injectable({
    providedIn: 'root'
})
export class I18nService {
    private translateService = inject(TranslateService);
    private rememberStateService = inject(RememberStateService);
    private queryService = inject(QueryService);
    private userService = inject(UserService);
    private toastrNotificationService = inject(ToastrNotificationService);
    private http = inject(HttpClient);

    currentLanguage = signal<Language>(null);
    languages: Language[] = [
        {
            languageId: LanguageIds.en,
            text: 'EN - English',
            iconClass: 'fi fi-us',
            click: () => this.changeDisplayLanguage(LanguageIds.en)
        },
        {
            languageId: LanguageIds.sl,
            text: 'SL - Slovenščina',
            iconClass: 'fi fi-si',
            click: () => this.changeDisplayLanguage(LanguageIds.sl)
        }
    ];
    isAuthenticated = false;

    constructor() {
        this.userService.isAuthenticatedSubject.pipe(distinctUntilChanged()).subscribe((value) => {
            this.isAuthenticated = value;
        });
        queueMicrotask(() => this.initialSetCurrentLanguageId());
    }

    private initialSetCurrentLanguageId() {
        const rememberedLanguageId = this.rememberStateService.get<LanguageIds>('languageId');
        if (rememberedLanguageId) {
            this.setLanguage(rememberedLanguageId.toLowerCase() as LanguageIds);
        } else if (this.translateService.currentLang) {
            this.setLanguage(this.translateService.currentLang.toLowerCase() as LanguageIds);
        } else {
            this.setLanguage(this.translateService.defaultLang.toLowerCase() as LanguageIds);
        }
    }

    private getLanguageById(id: LanguageIds) {
        return this.languages.find((language) => language.languageId === id);
    }

    /**
     * Change display language to the provided languageId.
     * For authenticated users also update the selected languageId in the database.
     */
    changeDisplayLanguage(languageId: LanguageIds): void {
        this.setLanguage(languageId);
        if (this.isAuthenticated) {
            this.setUserLanguageCommand(languageId);
        }
    }

    setLanguageFromUser(currentUser: User): void {
        const currentUserLanguageId = currentUser.languageId.toLowerCase() as LanguageIds;
        this.setLanguage(currentUserLanguageId);
    }

    private async setUserLanguageCommand(languageId: LanguageIds): Promise<void> {
        const data = {
            languageId: languageId.toUpperCase()
        };
        const result = await firstValueFrom(
            this.http.post<any>(`${environment.apiUrl}/authentication/SetUserLanguage`, data, { withCredentials: true })
        );

        if (!result) {
            this.toastrNotificationService.show({ type: 'error', message: 'Error while setting user language.' });
            return;
        }

        this.queryService.queryClient.invalidateQueries({ queryKey: [] });
    }

    private setLanguage(languageId: LanguageIds): void {
        this.translateService.use(languageId);
        this.rememberStateService.set('languageId', languageId);
        this.currentLanguage.set(this.getLanguageById(languageId));
    }
}
